<template>
  <div>
    <loading
      :active.sync="isLoading"
      color="#9de35c"
      loader="bars"
      :width="loadingSize"
      :height="loadingSize"
      :is-full-page="loadingFullPage"
    />

    <v-row class="mx-0 mb-2" align="center">
      <v-col cols="auto" class="py-1 py-sm-2">
        <v-btn text to="/">
          <v-icon left>mdi-arrow-left</v-icon> Back to Home
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col class="py-1 py-sm-2 text-right">
        <app-btn @click="openCreateDialog">
          <v-icon class="mr-1">mdi-plus</v-icon> Create Group
        </app-btn>
      </v-col>
    </v-row>

    <material-card
      icon="mdi-group"
      icon-small
      color="primary"
      title="Device Groups"
    >
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              hide-details
              label="Search groups"
              single-line
            />
          </v-col>
        </v-row>
        <v-divider class="mt-1" />

        <v-data-table
          :headers="tableHeaders"
          :items="groups"
          :search="search"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [5, 10, 15, 20],
          }"
        >
          <template v-slot:item.ownerCompanyName="{ item }">
            <span v-if="item.ownerCompanyName">{{ item.ownerCompanyName }}</span>
            <span v-else class="grey--text">All Companies (SuperUser)</span>
          </template>

          <template v-slot:item.deviceCount="{ item }">
            {{ item.effectiveDevices ? item.effectiveDevices.length : 0 }}
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small title="View Details" @click="openViewDialog(item)">
              <v-icon small color="info">mdi-eye</v-icon>
            </v-btn>
            <v-btn icon small title="Send Command" @click="openCommandDialog(item)">
              <v-icon small color="success">mdi-send</v-icon>
            </v-btn>
            <v-btn icon small title="View History" @click="viewHistory(item)">
              <v-icon small color="warning">mdi-history</v-icon>
            </v-btn>
            <v-btn icon small title="Edit" @click="openEditDialog(item)">
              <v-icon small color="primary">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small title="Delete" @click="confirmDelete(item)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialogEdit" max-width="700px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-alert v-if="formError" dense text type="error" class="mb-3">
              {{ formError }}
            </v-alert>

            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.name"
                  label="Group Name *"
                  :error-messages="nameError"
                  counter="100"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.description"
                  label="Description"
                  counter="500"
                  rows="2"
                />
              </v-col>
            </v-row>

            <!-- SuperUser only: Company selector -->
            <v-row v-if="isSuperUser">
              <v-col cols="12">
                <v-select
                  v-model="editedItem.ownerCompanyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Owner Company"
                  clearable
                  hint="Leave empty for all companies (SuperUser only)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editedItem.companyIds"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Include All Devices From Companies"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="All devices from selected companies will be included"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editedItem.deviceIds"
                  :items="availableDevices"
                  item-text="deviceName"
                  item-value="id"
                  label="Select Devices"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  :loading="loadingDevices"
                  @focus="loadDevices"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.deviceName || item.name || 'Unnamed Device' }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.macAddress }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" text :loading="saving" @click="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Details Dialog -->
    <v-dialog v-model="dialogView" max-width="900px" scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ viewedGroup.name || 'Device Group' }}</span>
          <v-spacer />
          <v-btn icon @click="dialogView = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <div v-if="viewLoading" class="py-8 text-center">
            <v-progress-circular indeterminate color="primary" size="36" />
          </div>

          <template v-else>
            <v-row class="mt-1">
              <v-col cols="12" md="7">
                <div class="group-view-row">
                  <span class="group-view-label">Description</span>
                  <span class="group-view-value">{{ viewedGroup.description || 'No description' }}</span>
                </div>
                <div class="group-view-row">
                  <span class="group-view-label">Owner Company</span>
                  <span class="group-view-value">
                    {{ viewedGroup.ownerCompanyName || 'All Companies (SuperUser)' }}
                  </span>
                </div>
                <div class="group-view-row">
                  <span class="group-view-label">Created</span>
                  <span class="group-view-value">{{ formatDate(viewedGroup.createdAt) }}</span>
                </div>
                <div class="group-view-row">
                  <span class="group-view-label">Last Updated</span>
                  <span class="group-view-value">{{ formatDate(viewedGroup.updatedAt) }}</span>
                </div>
                <div class="group-view-row">
                  <span class="group-view-label">Created By</span>
                  <span class="group-view-value group-view-value--id">
                    {{ viewedGroup.createdByUserId || '-' }}
                  </span>
                </div>
              </v-col>

              <v-col cols="12" md="5">
                <div class="group-view-stats">
                  <v-chip class="ma-1" color="primary" text-color="white">
                    <v-icon left small>mdi-devices</v-icon>
                    {{ viewedDevices.length }} Total Devices
                  </v-chip>
                  <v-chip class="ma-1" color="success" text-color="white">
                    <v-icon left small>mdi-check-circle</v-icon>
                    {{ viewedExplicitCount }} Explicit
                  </v-chip>
                  <v-chip
                    v-if="viewedByCompanyCount"
                    class="ma-1"
                    color="info"
                    text-color="white"
                  >
                    <v-icon left small>mdi-domain</v-icon>
                    {{ viewedByCompanyCount }} From Companies
                  </v-chip>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-3" />

            <div class="d-flex align-center mb-2">
              <h4 class="text-subtitle-1">Effective Devices</h4>
              <v-spacer />
              <v-text-field
                v-model="viewDeviceSearch"
                append-icon="mdi-magnify"
                label="Search devices"
                single-line
                hide-details
                dense
                style="max-width: 260px"
              />
            </div>

            <v-data-table
              :headers="viewDeviceHeaders"
              :items="viewedDevices"
              :search="viewDeviceSearch"
              :items-per-page="10"
              dense
              :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
              no-data-text="This group has no devices"
            >
              <template v-slot:item.name="{ item }">
                {{ item.name || 'Unnamed Device' }}
              </template>

              <template v-slot:item.macAddress="{ item }">
                <code>{{ item.macAddress || 'N/A' }}</code>
              </template>

              <template v-slot:item.companyName="{ item }">
                {{ item.companyName || `Company #${item.companyId}` }}
              </template>

              <template v-slot:item.membership="{ item }">
                <v-chip v-if="item.explicitMember" x-small color="success" text-color="white" class="mr-1">
                  Explicit
                </v-chip>
                <v-chip v-if="item.includedByCompany" x-small color="info" text-color="white">
                  By Company
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon small title="Track commands" @click="trackDeviceCommands(item)">
                  <v-icon small color="primary">mdi-swap-vertical-bold</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn text color="primary" @click="openFullGroupPage">
            <v-icon left small>mdi-open-in-new</v-icon> Open Full Page
          </v-btn>
          <v-spacer />
          <v-btn text @click="dialogView = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px">
      <v-card>
        <v-card-title class="text-h6">Delete Group</v-card-title>
        <v-card-text>
          Are you sure you want to delete the group "{{ deleteItem.name }}"?
          <br /><br />
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">Cancel</v-btn>
          <v-btn color="error" text :loading="deleting" @click="deleteGroup">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send Command Dialog -->
    <v-dialog v-model="dialogCommand" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Send Command to Group</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-alert v-if="commandError" dense text type="error" class="mb-3">
              {{ commandError }}
            </v-alert>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="commandItem.type"
                  :items="commandTypeItems"
                  label="Command Type *"
                  :loading="loadingCommandTypes"
                  :error-messages="commandTypeError"
                  required
                  @change="onCommandTypeChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.description">
                        {{ item.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              <v-col v-if="selectedCommand" cols="12">
                <command-payload-form
                  v-if="dialogCommand"
                  :group-id="currentCommandGroupId"
                  v-model="commandPayloadValues"
                  :command="selectedCommand"
                  :option-sources="commandCatalog.optionSources"
                  :company-id="payloadCompanyId"
                  :wifi-company-id="wifiCompanyId"
                  :company-ids="payloadCompanyIds"
                  :disabled="sendingCommand"
                  :show-errors="showPayloadErrors"
                  @validity="payloadValid = $event"
                />
              </v-col>
              <v-col v-else-if="commandItem.type" cols="12">
                <v-alert dense text type="warning" class="mb-0">
                  The catalog has no payload schema for "{{ commandItem.type }}",
                  so it will be sent with no payload.
                </v-alert>
              </v-col>
              <v-col v-if="commandItem.type" cols="12">
                <v-divider class="mb-3" />
                <command-expiry-picker
                  v-model="commandExpiry"
                  :default-info="selectedExpiryInfo"
                  :disabled="sendingCommand"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCommandDialog">Cancel</v-btn>
          <v-btn color="success" text :loading="sendingCommand" @click="sendCommand">
            Send Command
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Command Result Dialog -->
    <v-dialog v-model="dialogCommandResult" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Command Sent</span>
        </v-card-title>

        <v-card-text>
          <v-alert dense text type="success" class="mb-3">
            Command batch created successfully!
          </v-alert>

          <div v-if="commandResult">
            <p><strong>Batch ID:</strong> {{ commandResult.batchId }}</p>
            <p><strong>Target Devices:</strong> {{ commandResult.targetCount }}</p>
            <p><strong>Dispatched Immediately:</strong> {{ commandResult.dispatchedCount }}</p>
            <p><strong>Queued (offline):</strong> {{ commandResult.queuedCount }}</p>
            <p><strong>Failed:</strong> {{ commandResult.failedCount }}</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCommandResult = false">Close</v-btn>
          <v-btn color="primary" text @click="viewCommandDetails">View Details</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import MaterialCard from '@/components/MaterialCard'
import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'
import { singleDeviceCompanyId } from '@/util/wifiManagement'
import CommandPayloadForm from '@/views/components/CommandPayloadForm.vue'
import CommandExpiryPicker from '@/views/components/CommandExpiryPicker.vue'
import { buildExpiryFields, buildPayloadForCommand, commandSelectItems, isPayloadIncomplete, normalizeCatalog } from '@/util/commandCatalog'
import { mapGetters } from 'vuex'

export default {
  name: 'DeviceGroups',
  components: {
    Loading,
    MaterialCard,
    CommandPayloadForm,
    CommandExpiryPicker,
  },

  data() {
    return {
      isLoading: false,
      loadingFullPage: true,
      loadingSize: 64,
      loading: false,
      loadingDevices: false,

      search: '',
      groups: [],
      companies: [],
      availableDevices: [],

      tableHeaders: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Owner Company', value: 'ownerCompanyName', sortable: true },
        { text: 'Devices', value: 'deviceCount', sortable: false, align: 'center' },
        { text: 'Created', value: 'createdAt', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],

      dialogEdit: false,
      dialogView: false,
      viewLoading: false,
      viewedGroup: {},
      viewDeviceSearch: '',
      viewDeviceHeaders: [
        { text: 'Device Name', value: 'name', sortable: true },
        { text: 'MAC Address', value: 'macAddress', sortable: true },
        { text: 'Company', value: 'companyName', sortable: true },
        { text: 'Membership', value: 'membership', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      dialogDelete: false,
      dialogCommand: false,
      dialogCommandResult: false,

      editedIndex: -1,
      editedItem: {
        name: '',
        description: '',
        ownerCompanyId: null,
        deviceIds: [],
        companyIds: [],
      },
      defaultItem: {
        name: '',
        description: '',
        ownerCompanyId: null,
        deviceIds: [],
        companyIds: [],
      },

      formError: '',
      nameError: '',
      saving: false,

      deleteItem: {},
      deleting: false,

      commandItem: {
        type: '',
        payload: null,
        expiresAt: null,
      },
      commandCatalog: { commandTypes: [], commands: [], commandsByType: {}, optionSources: {} },
      loadingCommandTypes: false,
      commandPayloadValues: {},
      payloadValid: true,
      showPayloadErrors: false,
      commandExpiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      commandError: '',
      commandTypeError: '',
      sendingCommand: false,
      commandResult: null,
      currentCommandGroupId: null,
      currentCommandGroup: null,
    }
  },

  computed: {
    ...mapGetters('authUser', ['isSuperUser', 'currentCompanyId']),

    formTitle() {
      return this.editedIndex === -1 ? 'Create Device Group' : 'Edit Device Group'
    },

    commandTypeItems() {
      return commandSelectItems(this.commandCatalog).filter(item => item.value !== 'GetSavedWifiNetworks')
    },

    selectedCommand() {
      return this.commandCatalog.commandsByType[this.commandItem.type] || null
    },

    // Per-type expiry defaults, published alongside the descriptors.
    selectedExpiryInfo() {
      return (this.commandCatalog.expiryByType || {})[this.commandItem.type] || null
    },

    viewedDevices() {
      return this.viewedGroup.effectiveDevices || []
    },

    viewedExplicitCount() {
      return this.viewedDevices.filter(device => device.explicitMember).length
    },

    viewedByCompanyCount() {
      return this.viewedDevices.filter(device => device.includedByCompany).length
    },

    // Option sources are scoped by company; use the group's owner where it has one.
    wifiCompanyId() {
      return singleDeviceCompanyId((this.currentCommandGroup && this.currentCommandGroup.effectiveDevices) || [])
    },

    payloadCompanyId() {
      return (this.currentCommandGroup && this.currentCommandGroup.ownerCompanyId) || this.currentCompanyId
    },

    // A group can span companies, and app lists are per company. Hand the form
    // every company it targets so option lists can be intersected.
    payloadCompanyIds() {
      const devices = (this.currentCommandGroup && this.currentCommandGroup.effectiveDevices) || []
      return [...new Set(devices.map(device => device.companyId).filter(Boolean))]
    },
  },

  mounted() {
    this.loadGroups()
    this.loadCompanies()
    this.loadCommandTypes()
  },

  methods: {
    async loadGroups() {
      this.loading = true
      try {
        const response = await DeviceGroupsDataService.getAll()
        this.groups = response.data || []
      } catch (error) {
        this.$toast.error('Failed to load device groups')
        console.error('Error loading groups:', error)
      } finally {
        this.loading = false
      }
    },

    async loadCompanies() {
      if (!this.isSuperUser) return
      try {
        await this.$store.dispatch('company/loadCompanies', 0)
        this.companies = this.$store.getters['company/getCompanies']
      } catch (error) {
        console.error('Error loading companies:', error)
      }
    },

    async loadDevices() {
      if (this.availableDevices.length > 0) return
      this.loadingDevices = true
      try {
        const response = await this.$http.get('Company/DevicePaginationByCompany', {
          params: {
            companyId: this.currentCompanyId,
            page: 1,
            limit: 1000
          }
        })
        this.availableDevices = this.normalizeDevices(response.data)
      } catch (error) {
        console.error('Error loading devices:', error)
      } finally {
        this.loadingDevices = false
      }
    },

    async loadCommandTypes() {
      this.loadingCommandTypes = true
      try {
        const response = await DeviceGroupsDataService.getCommandTypes()
        this.commandCatalog = normalizeCatalog(response.data)
      } catch (error) {
        console.error('Error loading command catalog:', error)
        this.commandCatalog = { commandTypes: [], commands: [], commandsByType: {}, optionSources: {} }
      } finally {
        this.loadingCommandTypes = false
      }
    },

    onCommandTypeChange() {
      // Each command declares its own fields; start its payload from scratch.
      this.commandPayloadValues = {}
      this.showPayloadErrors = false
      this.commandTypeError = ''
      // Expiry defaults are per type, so fall back to the server default.
      this.commandExpiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
    },

    // Devices come back with `name`, but the pickers key their text off
    // `deviceName` -- without it the selection chips render empty.
    normalizeDevices(data) {
      if (!Array.isArray(data)) return []

      return data.map(device => ({
        ...device,
        deviceName: device.deviceName || device.name || device.macAddress || `Device #${device.id}`,
      }))
    },

    openCreateDialog() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
      this.formError = ''
      this.nameError = ''
      this.dialogEdit = true
      this.loadDevices()
    },

    openEditDialog(item) {
      this.editedIndex = item.id
      this.editedItem = {
        name: item.name,
        description: item.description || '',
        ownerCompanyId: item.ownerCompanyId,
        deviceIds: item.explicitDeviceIds || [],
        companyIds: item.companyIds || [],
      }
      this.formError = ''
      this.nameError = ''
      this.dialogEdit = true
      // Preload so the chips for already-selected devices resolve to a name
      // instead of waiting for the picker to be focused.
      this.loadDevices()
    },

    closeDialog() {
      this.dialogEdit = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    validateForm() {
      this.nameError = ''
      let isValid = true

      if (!this.editedItem.name || this.editedItem.name.trim() === '') {
        this.nameError = 'Name is required'
        isValid = false
      } else if (this.editedItem.name.length > 100) {
        this.nameError = 'Name must be less than 100 characters'
        isValid = false
      }

      return isValid
    },

    async save() {
      if (!this.validateForm()) return

      this.saving = true
      this.formError = ''

      try {
        const payload = {
          name: this.editedItem.name.trim(),
          description: this.editedItem.description ? this.editedItem.description.trim() : null,
          deviceIds: this.editedItem.deviceIds || [],
        }

        if (this.isSuperUser) {
          payload.ownerCompanyId = this.editedItem.ownerCompanyId || null
          payload.companyIds = this.editedItem.companyIds || []
        }

        if (this.editedIndex === -1) {
          await DeviceGroupsDataService.create(payload)
          this.$toast.success('Device group created successfully')
        } else {
          await DeviceGroupsDataService.update(this.editedIndex, payload)
          await DeviceGroupsDataService.updateMembers(this.editedIndex, {
            deviceIds: payload.deviceIds,
            companyIds: payload.companyIds || [],
          })
          this.$toast.success('Device group updated successfully')
        }

        this.closeDialog()
        this.loadGroups()
      } catch (error) {
        this.formError = error.response && error.response.data ? error.response.data.message : 'Failed to save device group'
        console.error('Error saving group:', error)
      } finally {
        this.saving = false
      }
    },

    confirmDelete(item) {
      this.deleteItem = item
      this.dialogDelete = true
    },

    async deleteGroup() {
      this.deleting = true
      try {
        await DeviceGroupsDataService.delete(this.deleteItem.id)
        this.$toast.success('Device group deleted successfully')
        this.dialogDelete = false
        this.loadGroups()
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : 'Failed to delete group')
        console.error('Error deleting group:', error)
      } finally {
        this.deleting = false
      }
    },

    async openViewDialog(item) {
      // Show what the list already has, then refresh from the detail endpoint
      // so effectiveDevices is current.
      this.viewedGroup = item
      this.viewDeviceSearch = ''
      this.dialogView = true
      this.viewLoading = true

      try {
        const response = await DeviceGroupsDataService.get(item.id)
        this.viewedGroup = response.data || item
      } catch (error) {
        console.error('Error loading group:', error)
        this.$toast.error('Failed to load group details')
      } finally {
        this.viewLoading = false
      }
    },

    openFullGroupPage() {
      this.dialogView = false
      this.$router.push('/device-groups/' + this.viewedGroup.id)
    },

    trackDeviceCommands(device) {
      const query = {}
      if (device.deviceId) query.deviceId = String(device.deviceId)
      if (device.macAddress) query.mac = device.macAddress
      this.$router.push({ path: '/device-commands', query })
    },

    viewHistory(item) {
      this.$router.push('/device-groups/' + item.id + '/history')
    },

    async openCommandDialog(item) {
      this.currentCommandGroupId = item.id
      this.currentCommandGroup = item
      this.commandItem = {
        type: '',
        payload: null,
        expiresAt: null,
      }
      this.commandPayloadValues = {}
      this.payloadValid = true
      this.showPayloadErrors = false
      this.commandExpiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
      this.commandError = ''
      this.commandTypeError = ''
      this.dialogCommand = true

      // Option sources are company-scoped and the companies come from
      // effectiveDevices, so refresh from the detail endpoint rather than
      // trusting whatever the list row happened to carry.
      try {
        const response = await DeviceGroupsDataService.get(item.id)
        if (response.data) {
          this.currentCommandGroup = response.data
        }
      } catch (error) {
        console.error('Error loading group for command dialog:', error)
      }
    },

    closeCommandDialog() {
      this.dialogCommand = false
      this.currentCommandGroupId = null
      this.currentCommandGroup = null
    },

    // ValidationProblemDetails puts the useful text in `errors`, not `message`,
    // so flatten it rather than showing a bare "Failed to send command".
    describeSendError(error) {
      const data = error && error.response ? error.response.data : null

      if (!data) {
        return (error && error.message) || 'Failed to send command'
      }

      if (data.errors && typeof data.errors === 'object') {
        const details = Object.keys(data.errors)
          .map(field => {
            const messages = data.errors[field]
            const text = Array.isArray(messages) ? messages.join(' ') : String(messages)
            return `${field}: ${text}`
          })
          .join(' | ')

        if (details) {
          return details
        }
      }

      return data.message || data.title || data.detail || 'Failed to send command'
    },

    validateCommand() {
      this.commandTypeError = ''
      this.showPayloadErrors = false
      let isValid = true

      if (!this.commandItem.type) {
        this.commandTypeError = 'Command type is required'
        isValid = false
      }

      // The payload form reports its own validity off the catalog's required flags.
      if (!this.payloadValid) {
        this.showPayloadErrors = true
        isValid = false
      }

      // The server binds payload to JsonElement? and rejects a null one for
      // commands that declare fields, so catch it before the round trip.
      if (isValid && isPayloadIncomplete(this.selectedCommand, this.commandPayloadValues)) {
        this.commandError = `${this.commandItem.type} needs a payload. Fill in the fields above.`
        this.showPayloadErrors = true
        isValid = false
      }

      return isValid
    },

    async sendCommand() {
      if (!this.validateCommand()) return

      this.sendingCommand = true
      this.commandError = ''

      try {
        const payload = {
          type: this.commandItem.type,
          // The API binds this to JsonElement?, so send real JSON. Shape comes
          // from the catalog: none -> null, scalar -> bare value, object -> object.
          payload: buildPayloadForCommand(this.selectedCommand, this.commandPayloadValues),
          // Either ttlMinutes or expiresAt, or neither to take the per-type default.
          ...buildExpiryFields(this.commandExpiry),
        }

        const response = await DeviceGroupsDataService.sendCommand(this.currentCommandGroupId, payload)
        this.commandResult = response.data
        this.closeCommandDialog()
        this.dialogCommandResult = true
        this.$toast.success('Command sent successfully')
      } catch (error) {
        this.commandError = this.describeSendError(error)
        console.error('Error sending command:', error)
      } finally {
        this.sendingCommand = false
      }
    },

    viewCommandDetails() {
      this.dialogCommandResult = false
      this.$router.push('/device-groups/' + this.currentCommandGroupId + '/history')
    },

    formatDate(date) {
      if (!date) return '-'
      // Naive UTC in, local out.
      return this.$format.dateTime(date)
    },
  },
}
</script>

<style scoped>
.group-view-row {
  display: flex;
  gap: 12px;
  padding: 4px 0;
  font-size: 0.9rem;
}

.group-view-label {
  width: 130px;
  flex: none;
  color: rgba(0, 0, 0, 0.6);
}

.group-view-value {
  min-width: 0;
  word-break: break-word;
}

.group-view-value--id {
  font-family: monospace;
  font-size: 0.8rem;
}

.group-view-stats {
  display: flex;
  flex-wrap: wrap;
}

.devices-toolbar-row {
  background: transparent;
}

.devices-toolbar-btn {
  margin-left: 8px;
}
</style>
